import random from 'lodash/random';
import { createPostGraphqlRequest } from '@tv2/lib-core/request';
import { addParams } from '@tv2/lib-core/querystring';
import JWT from '@tv2/lib-core/jwt';
import UnifiedPlayer from '@tv2-player/unifiedplayer/src/core/UnifiedPlayer';
import {
  mapAdobeTrackingObject,
  mapNielsenObject,
} from '@tv2-player/unifiedplayer/src/core/mapTracking';
import UAParser from 'ua-parser-js';
import { AdVendor, Media, MediaCapabilities } from '@tv2-player/unifiedplayer/src/types';
import { ChromecastUser } from '@tv2-player/unifiedplayer/src/types/ChromecastUser';
import notNullish from '@tv2/lib-core/notNullish';
import { getFirstCapabilityValue } from '@tv2-player/unifiedplayer/src/util/hasCapability';
import { onEither } from '@tv2/lib-graphql/typeHelper';
import { switchStreamURL } from '@tv2-player/unifiedplayer/src/mediaMaking/switchStreamUrl';
import { UUIDGenerator } from '@tv2-player/unifiedplayer/src/util/UUIDGenerator';
import { manipulateAdUrl } from '@tv2-player/unifiedplayer/src/mediaMaking/switchPrerollHost';
import { CapabilityType, Drm, EntityType, Format } from '../__generated__/api/types';
import { deviceDetector } from '../utils/deviceDetector';
import endpoints from '../config/endpoints';
import cast from '../types/chromecast-caf-receiver';
import {
  PlayChromecastContentPlayback,
  PlayChromecastContentPlaybackQuery,
} from './__generated__/Media';
import buildWatermarkImage from '../utils/buildWatermarkImage';
import { LoadRequestData } from '../types/chromecast-caf-receiver/cast.framework.messages';
import { UnifiedPlayerNextFragment } from '../sharedFragments/__generated__/fragments';

const uaParser = new UAParser();

const {
  GenericMediaMetadata,
  MediaInformation,
  MovieMediaMetadata,
  VastAdsRequest,
  StreamType,
  Image,
} = cast.framework.messages;

const operationName = 'play_chromecast_content_Playback';
const deviceGeneration = deviceDetector();

const defaultHeaders = {
  // This is the base platform of the client device (e.g. Web, iOS, SmartTV, etc.)
  'Client-Platform': 'chromecast',
  // This is the version of the client application
  'Client-Platform-AppVersion': UnifiedPlayer.version,
  // This is the intended audience of the client device (e.g. PLAY, NEWS, Sport, Weather, etc.)
  'Client-Platform-Audience': 'Play',
  // This is the classification of the client device (e.g. LG, Samsung for SmartTV or Chrome, Firefox for Web)
  'Client-Platform-Class': deviceGeneration,

  'Client-Platform-OS-Version': uaParser.getOS().version,
  'Client-Platform-Model-Number': deviceGeneration,
  'Client-Platform-Marketing-Name': uaParser.getDevice().model,
};

/**
 * Returns true if entity is a broadcast.
 * @param type The entity type returned from GraphQL service.
 */
export function isBroadcast(type: EntityType | null | undefined) {
  if (!type) return false;
  return ['BROADCAST', 'EVENT'].includes(type);
}

/**
 * Returns updated src where `cenc` is replaced with the value of query parameter `cenc` if it exists.
 * @param {string} src The streaming src, where cenc should be replaced.
 */
function updateCencInSrcWithQueryParamCenc(src: string) {
  const CENC_KEY = 'cenc';
  const urlSearchParams = new URLSearchParams(window.location.search);
  if (urlSearchParams.has(CENC_KEY)) {
    const cencUpdateValue = urlSearchParams.get(CENC_KEY);
    return src.replace('/cenc/', `/${cencUpdateValue}/`);
  }
  return src;
}

function getChannel(entity: PlayChromecastContentPlaybackQuery['entity']) {
  if (isBroadcast(entity?.type)) {
    return entity?.presentationTitle ?? null;
  }

  if (entity && 'contentProvider' in entity) {
    if (entity.contentProvider && 'broadcast' in entity.contentProvider) {
      return entity.contentProvider.broadcast?.broadcastName ?? null;
    }
  }

  return null;
}

/**
 * Maps entity information from GraphQL to a UnifiedPlayer metadata object.
 * @param entity The entity object returned from GraphQL service.
 * @param duration
 * @return The metadata object compatible with UnifiedPlayer.
 */
function mapEntityToMetadata(
  entity: Exclude<PlayChromecastContentPlaybackQuery['entity'], null>,
  duration?: number
) {
  const channel = getChannel(entity);

  const { start, stop } = { start: undefined, stop: undefined, ...entity.referred };

  const liveEvent = entity.referred && {
    start,
    stop,
  };

  const { seriesGuid, seriesTitle, episodeNumber, seasonNumber, epgEntries } = {
    seriesGuid: undefined,
    seriesTitle: undefined,
    episodeNumber: undefined,
    seasonNumber: undefined,
    epgEntries: undefined,
    ...entity,
  };

  return {
    guid: entity.guid,
    channel,
    entityTitle: entity.title,
    title: epgEntries?.nodes?.filter(notNullish)[0]?.title ?? entity.presentationTitle,
    subtitle: entity.presentationSubtitle,
    seriesGuid,
    seriesTitle,
    episode: episodeNumber,
    season: seasonNumber,
    image: getPresentationArt(entity),
    teaserWatermark: entity.teaser?.watermark,
    isLive: isBroadcast(entity.type),
    type: entity.type,
    liveEvent,
    duration,
    contentProvider: 'contentProvider' in entity ? entity.contentProvider?.guid! : undefined,
  };
}

function getPresentationArt(entity: Exclude<PlayChromecastContentPlaybackQuery['entity'], null>) {
  switch (entity.__typename) {
    case 'Episode':
      return entity.series?.presentationArt ?? null;
    case 'Movie':
      return entity.art;
    case 'Program':
    case 'Broadcast':
    case 'Event':
      return entity.presentationArt;
    default:
      return null;
  }
}

/**
 * Maps entity information from GraphQL to a UnifiedPlayer metadata object.
 * @param entity The entity object returned from GraphQL service.
 * @param duration
 * @return The metadata object compatible with UnifiedPlayer.
 */
function mapNextEntityToMetadata(entity: UnifiedPlayerNextFragment, duration?: number) {
  return {
    guid: entity.guid,
    channel: null,
    title: entity.presentationTitle,
    subtitle: entity.presentationSubtitle,
    episode: entity.episodeNumber,
    season: entity.seasonNumber,
    image: entity.presentationArt,
    isLive: false,
    duration,
    type: entity.type,
  };
}

export function makeMediaObject(
  response: PlayChromecastContentPlaybackQuery,
  user: ChromecastUser,
  request: LoadRequestData,
  mediaCapabilities: MediaCapabilities
) {
  const media = mapResultToMedia(request, response, user, mediaCapabilities);
  return prepareMedia(media, user, request);
}

/**
 * Maps GraphQL result object to a UnifiedPlayer media object.
 */
export function mapResultToMedia(
  request: LoadRequestData,
  { entity, media, tracking }: PlayChromecastContentPlaybackQuery,
  user: ChromecastUser,
  mediaCapabilities: MediaCapabilities
) {
  const { subtitles, concurrency, duration } = media!;

  const streams = switchStreamURL(media!.streams);
  // Take the first stream as the backend priorities the steams.
  const stream = streams[0];
  const { drmLicense, format, ad } = stream!;

  let src: string = stream!.url!;

  src = updateCencInSrcWithQueryParamCenc(src!);
  const { resettableDeviceId, adTrackingLimited, resettableDeviceIdType } = user;

  const isYospace =
    stream!.capabilities.find(capability => capability?.values?.includes(AdVendor.YOSPACE_CS)) !==
    undefined;

  if (isYospace && adTrackingLimited === '0') {
    src = addParams(
      src,
      new Map([
        ['rdid', resettableDeviceId],
        ['is_lat', adTrackingLimited],
        ['idtype', resettableDeviceIdType],
      ])
    )!;
  }

  const sid = UUIDGenerator.sessionId(resettableDeviceId);
  if (isYospace) {
    src = addParams(
      src,
      new Map([
        ['yo.av', '3'],
        ['yo.ac', 'true'],
        ['sid', sid],
      ])
    )!;
  }

  const metadata = mapEntityToMetadata(entity!, duration!);
  const queue =
    entity && 'next' in entity && entity.next
      ? [{ metadata: mapNextEntityToMetadata(entity.next) }]
      : undefined;

  const dynamicRange = getFirstCapabilityValue(stream?.capabilities, 'DYNAMICRANGE') ?? 'SDR';

  const vodEntity = onEither(entity, ['Episode', 'Movie', 'Program']);

  const clientPlatformModelNumber = deviceGeneration;
  const clientPlatformMarketingName = uaParser.getDevice().model;
  const clientPlatformOsVersion = uaParser.getOS().version;
  const ads = ad && { ...ad, url: addParams(ad?.url, new Map([['sid', sid]])) };

  if (ads?.url) {
    ads.url = manipulateAdUrl(ads.url);
  }

  return {
    src,
    format,
    drm: drmLicense,
    ads,
    metadata,
    tracking: {
      adobe: tracking?.adobe,
      nielsen: tracking?.nielsen,
      npaw: {
        viewerId: user.npawId,
        presentationTitle: entity?.presentationTitle ?? '',
        guid: entity?.guid,
        genre:
          vodEntity?.genres?.nodes?.join(', ') ??
          vodEntity?.categories?.nodes?.join(', ') ??
          undefined,
        type: entity?.type ?? undefined,
        deviceBlacklistString: `${clientPlatformModelNumber}#${clientPlatformMarketingName}#${clientPlatformOsVersion}`,
        region: request.customData.region,
      } satisfies Media['tracking']['npaw'],
    },
    queue,
    concurrency,
    texttracks: subtitles,
    dynamicRange,
    sourceCapabilities: stream!.capabilities,
    type: mediaCapabilities.sourceType,
    mediaCapabilities,
  };
}

/**
 * Updates the UnifiedPlayer media object with the custom data received from sender. Does not
 * mutate the original media object, but references still exist as it is only shallowly cloned.
 * @param  {object} media      The UnifiedPlayer media object.
 * @param  {object} user       User.
 * @param  {object} request    Sender request.
 * @return {object}            New UnifiedPlayer media object with custom data added.
 */
export function prepareMedia(
  media: ReturnType<typeof mapResultToMedia>,
  user: ChromecastUser,
  request: LoadRequestData
) {
  const { accessToken, userId, adobeId } = user;
  const { adobe, nielsen } = media.tracking ?? {};

  const { metadata } = media;

  const {
    customData: { playbackInitiationType = null },
  } = request;

  const isFallbackTracking = media.tracking?.adobe == null;
  const adobeTrackingObject = mapAdobeTrackingObject(
    adobe,
    {
      contentProvider: metadata.contentProvider ?? null,
      adobeTraceId: adobeId!,
      userId,
      initType: playbackInitiationType,
      platform: 'chromecast',
    },
    isFallbackTracking,
    metadata.guid,
    {
      profileId: user.selectedProfile?.profileId?.toString() ?? '',
      profileTraceId: user.selectedProfile?.profileTraceId ?? '',
      profileType: user.selectedProfile?.profileType ?? '',
      profileInfo: user.selectedProfile?.profileInfo ?? '',
    }
  );

  const nielsenTrackingObject = mapNielsenObject(nielsen, {
    contentProvider: metadata.contentProvider,
    isLive: metadata.isLive ?? false,
  });

  return {
    ...media,
    user: {
      accessToken,
      isJWT: JWT.isJWT(accessToken),
      isLoggedIn: Boolean(accessToken),
      selectedProfile: user.selectedProfile
        ? { profileToken: user.selectedProfile.profileToken }
        : null,
    },
    profile: user.selectedProfile,
    tracking: media.tracking && {
      nielsen: nielsenTrackingObject,
      adobe: adobeTrackingObject,
      npaw: media.tracking.npaw,
    },
  };
}

/**
 * Maps a UnifiedPlayer media object to a cast.framework.messages.MediaInformation object.
 */
export function mapMediaToMediaInformation(
  media: {
    user?: {
      accessToken: string;
      isJWT: boolean;
      isLoggedIn: boolean;
      selectedProfile: { profileToken: string } | null;
    };
    profile?: {
      profileToken: string;
      profileTraceId: string;
      profileId: number;
      profileType: string;
      profileInfo: string;
    } | null;
    tracking?: {
      nielsen: any;
      adobe: any;
    };
    src?: any;
    format?: Format | null;
    drm?: { __typename: 'DrmLicense'; drm: Drm; url: string; token: string } | null;
    ads?: any;
    metadata: any;
    queue?:
      | {
          metadata: {
            guid: string;
            channel: null;
            title: string | null;
            subtitle: string | null;
            episode: number | null;
            season: number | null;
            image: { __typename: 'Art'; url: string | null } | null;
            isLive: boolean;
            duration: number | undefined;
            type: EntityType | null;
          };
        }[]
      | undefined;
    concurrency?: {
      __typename: 'Concurrency';
      updateInterval: number | null;
      httpMethod: string | null;
      url: string | null;
      body: {
        __typename: 'ConcurrencyBody';
        token: string | null;
        progress: number | null;
        guid: string | null;
        userAction: string | null;
        concurrencyAction: string | null;
      } | null;
    } | null;
    texttracks?: ({ __typename: 'Subtitle'; useAsDefault: boolean | null } | null)[];
    dynamicRange?: string;
    sourceCapabilities?: ({
      __typename: 'Capability';
      capabilityType: CapabilityType | null;
      values: Array<string>;
    } | null)[];
    type?: any;
  },
  currentTimeIsSet = false
) {
  const {
    src: contentUrl,
    type: contentType,
    ads,
    metadata: { guid: contentId },
  } = media;
  const adTagUrl = ads?.url;

  const vmapAdsRequest =
    adTagUrl &&
    !currentTimeIsSet &&
    Object.assign(new VastAdsRequest(), {
      adTagUrl: `${adTagUrl}&pp=chromecast&correlator=${random(100000000000000000)}`,
    });

  return Object.assign(new MediaInformation(), {
    contentId,
    contentUrl,
    contentType,
    streamType: media.metadata.isLive ? StreamType.LIVE : StreamType.BUFFERED,
    metadata: mapMetadataToMediaInformation(media),
    vmapAdsRequest,
  });
}

/**
 * Mapping metadata fetched from graphQL, into readable object for CAF.
 * @param  {object} media Graphql media object.
 * @return {MovieMediaMetadata|TvShowMediaMetadata|GenericMediaMetadata}       CAF metadata object.
 */
function mapMetadataToMediaInformation(media?: any) {
  const { title, subtitle, type, image, teaserWatermark } = media.metadata;

  const images = [];

  if (image != null) images.push(new Image(buildWatermarkImage(image, teaserWatermark)));

  if (type === 'MOVIE') {
    return Object.assign(new MovieMediaMetadata(), {
      title,
      images,
      subtitle,
    });
  }

  return Object.assign(new GenericMediaMetadata(), {
    title,
    images,
    subtitle,
  });
}

/**
 * Request grapql, used for fetching media.
 * @param {string} playbackRequest   Graphql request.
 * @param {object} variables         Specific variables, like guid and clientId.
 * @param {object} user              User information.
 * @param mediaCapabilities
 * @param {object} headers           Optional.
 * @return {Promise}                 Promise resolving with media object or rejecting with error.
 * @fulfil {Object}
 * @reject {cast.framework.messages.ErrorData}

 */
async function graphqlRequest(
  playbackRequest: string,
  variables: any,
  user: any,
  mediaCapabilities: MediaCapabilities,
  headers = {}
) {
  return createPostGraphqlRequest<PlayChromecastContentPlaybackQuery>(
    endpoints.api,
    playbackRequest,
    { ...variables, ...mediaCapabilities },
    operationName,
    { ...headers, ...(defaultHeaders as HeadersInit) }
  ).catch(response => {
    if (response.data?.media?.streams) {
      return response;
    }
    throw response;
  });
}
/**
 * Requests playback information from GraphQL. Returns a promise either resolving to an object
 * of media information, or rejecting with an ErrorData object.
 * @param  {string} guid             Id of the content.
 * @param  {string} uniqueId         Unique id of the request.
 * @param  {string} jwtToken         Token used then making a selector request.
 * @param  {object} user             User information.
 * @param mediaCapabilities
 * @return {Promise}                 GraphqlRequest.
 */
export function fetchMedia(
  guid: string,
  uniqueId: string,
  jwtToken: string,
  user: ChromecastUser,
  mediaCapabilities: MediaCapabilities
) {
  const { device } = user;
  const variables = {
    guid,
    deviceId: device.deviceUid,
    deviceDescription: device.deviceName,
    nielsenTrackingVersion: '2',
  };

  return graphqlRequest(PlayChromecastContentPlayback, variables, user, mediaCapabilities, {
    ...(jwtToken
      ? {
          Authorization: `Bearer ${jwtToken}`,
          'Client-Platform-User-Profile': user.selectedProfile?.profileToken ?? '',
        }
      : null),
  });
}
